/* global Image */

import PropTypes from 'prop-types'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'

import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

import DeckCarousel from './DeckCarousel'
import DisabledDeck from './DisabledDeck'

const styles = (theme) => ({
  centeredContainer: {
    alignItems: 'center',
    color: '#ffffff',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  loadingIndicator: {
    margin: theme.spacing(2),
  },
  centeredText: {
    color: '#ffffff',
    textAlign: 'center',
  },
  carouselContainer: {
    height: '100%',
    position: 'relative',
  },
})
const useStyles = makeStyles(styles)

function loadSlide(slide) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.src = slide.image
    image.onload = resolve
    image.onerror = reject
  })
}

function useSlidesCached(slides, status) {
  const [slidesCached, setSlidesCached] = useState(false)

  const setSlidesCachedRef = useRef()
  useEffect(() => {
    setSlidesCachedRef.current = setSlidesCached
    return () => {
      setSlidesCachedRef.current = () => {}
    }
  }, [setSlidesCached])

  useEffect(() => {
    if (status === 'success') {
      Promise.all(slides.map(loadSlide))
        .catch(() => {})
        .then(() => setSlidesCachedRef.current(true))
    }
  }, [setSlidesCachedRef, slides, status])

  return slidesCached
}

function useDeckHotkeys(keys, callback, deps) {
  return useHotkeys(
    keys,
    (event, handler) => {
      event.preventDefault()

      callback(event, handler)
    },
    {},
    deps,
  )
}

export default function Deck(props) {
  const { t } = useTranslation()

  const {
    controls = true,
    currentSlide = 0,
    disabled = false,
    disabledMessage = t('deck:messages-isDisabled'),
    onSlideChange,
    slides,
    status,
    statusProgress,
  } = props

  const slidesLength = slides.length

  const [slideIndex, setSlideIndex] = useState(currentSlide)

  useEffect(() => {
    setSlideIndex(currentSlide)
  }, [currentSlide, setSlideIndex])

  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(slideIndex)
    }
  }, [slideIndex, onSlideChange])

  const goToPreviousSlide = useCallback(() => {
    if (controls) {
      setSlideIndex(
        (prevState) => (slidesLength + (prevState - 1)) % slidesLength,
      )
    }
  }, [controls, setSlideIndex, slidesLength])

  const goToNextSlide = useCallback(() => {
    if (controls) {
      setSlideIndex(
        (prevState) => (slidesLength + (prevState + 1)) % slidesLength,
      )
    }
  }, [controls, setSlideIndex, slidesLength])

  useDeckHotkeys('left', goToPreviousSlide, [goToPreviousSlide])
  useDeckHotkeys('right', goToNextSlide, [goToNextSlide])

  const slidesCached = useSlidesCached(slides, status)

  const classes = useStyles()

  if (disabled) {
    return <DisabledDeck message={disabledMessage} />
  }

  return (
    <Fragment>
      {status === 'error' && (
        <div className={classes.centeredContainer}>
          {t('deck:messages-isErrored')}
        </div>
      )}

      {(status === 'pending' ||
        status === 'processing' ||
        (status === 'success' && !slidesCached)) && (
        <div className={classes.centeredContainer}>
          <CircularProgress
            className={classes.loadingIndicator}
            value={statusProgress}
            variant={
              status === 'processing' && statusProgress > 0
                ? 'static'
                : 'indeterminate'
            }
          />

          <Typography className={classes.centeredText} variant="body1">
            {status === 'success' && !slidesCached
              ? t('deck:messages-isLoading')
              : t('deck:messages-isProcessing')}
            {statusProgress > 0 && statusProgress < 100 && (
              <span> ({statusProgress.toFixed(0)}%)</span>
            )}
          </Typography>
        </div>
      )}

      {status === 'success' && slidesCached && (
        <div className={classes.carouselContainer}>
          <DeckCarousel
            controls={controls}
            currentSlide={slideIndex}
            onNextSlide={goToNextSlide}
            onPreviousSlide={goToPreviousSlide}
            slides={slides}
          />
        </div>
      )}
    </Fragment>
  )
}

Deck.propTypes = {
  controls: PropTypes.bool,
  currentSlide: PropTypes.number,
  disabled: PropTypes.bool,
  disabledMessage: PropTypes.string,
  onSlideChange: PropTypes.func,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
    }),
  ).isRequired,
  status: PropTypes.string.isRequired,
  statusProgress: PropTypes.number.isRequired,
}
